const WATERMARK_FONT_WEIGHTS = [400, 500, 600, 700, 800];

// Same time/date formats as the mobile app (India time, 12-hour clock).
export const fmtClock = (d) =>
  d.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true });
export const fmtDateIN = (d) =>
  d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short", month: "short", day: "numeric", year: "numeric" });

// Aspect ratio (width / height) of the captured photo and of the preview box.
export const PHOTO_ASPECT = 3 / 4;

// Actively triggers loading of every font weight the watermark uses and
// waits for it, so canvas text is never drawn with a font that is still
// loading (which can render as nothing on WebKit/Safari).
export async function ensureWatermarkFontLoaded() {
  if (!document.fonts || !document.fonts.load) return;
  try {
    await Promise.all(
      WATERMARK_FONT_WEIGHTS.map((weight) =>
        document.fonts.load(`${weight} 16px "Plus Jakarta Sans"`)
      )
    );
  } catch (e) {
    console.log("Watermark font load error:", e);
  }
}

// Returns the center-crop rectangle of a video frame that matches PHOTO_ASPECT,
// so the saved photo looks exactly like the preview (no edge text cropped off).
export function getCropRect(videoWidth, videoHeight) {
  let sw = videoWidth;
  let sh = videoHeight;
  let sx = 0;
  let sy = 0;
  if (videoWidth / videoHeight > PHOTO_ASPECT) {
    sw = Math.round(videoHeight * PHOTO_ASPECT);
    sx = Math.round((videoWidth - sw) / 2);
  } else {
    sh = Math.round(videoWidth / PHOTO_ASPECT);
    sy = Math.round((videoHeight - sh) / 2);
  }
  return { sx, sy, sw, sh };
}

// Draws the DB Skills attendance watermark at the bottom of the canvas.
// Everything scales with the photo width so it stays neat at any resolution,
// and the box is sized to its content (no empty space).
export function drawWatermark(ctx, opts) {
  const {
    width,
    height,
    statusText,   // e.g. "✓ CHECKED IN"
    statusColor,  // e.g. "#4ADE80"
    time,
    date,
    name,
    location,
    address,
  } = opts;

  // 720px-wide portrait photo is the reference size.
  const scale = Math.max(0.6, Math.min(3, width / 720));

  const margin = 10 * scale;               // gap between box and photo edge
  const padX = 18 * scale;                 // inner left/right padding
  const boxH = 142 * scale;                // fits 4 rows
  const boxX = margin;
  const boxW = width - margin * 2;
  const boxY = height - margin - boxH;

  const row1 = boxY + 30 * scale;
  const divY = boxY + 44 * scale;
  const row2 = boxY + 72 * scale;
  const row3 = boxY + 100 * scale;
  const row4 = boxY + 126 * scale;

  const font = (weight, size) =>
    `${weight} ${size * scale}px 'Plus Jakarta Sans', -apple-system, 'Segoe UI', Arial, sans-serif`;

  // Dark glass background box
  ctx.fillStyle = "rgba(8, 16, 36, 0.88)";
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.strokeStyle = "rgba(6, 182, 212, 0.45)";
  ctx.lineWidth = 1.5 * scale;
  ctx.strokeRect(boxX, boxY, boxW, boxH);

  ctx.textBaseline = "alphabetic";

  // ── Row 1: Brand (left) + Status/Time (right) ──
  ctx.textAlign = "left";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font("bold", 20);
  const brandX = boxX + padX;
  ctx.fillText("DB Skills", brandX, row1);
  const brandWidth = ctx.measureText("DB Skills").width;

  ctx.fillStyle = "rgba(6, 182, 212, 0.95)";
  ctx.font = font("600", 12);
  ctx.fillText("Attendance System", brandX + brandWidth + 10 * scale, row1);

  const rightX = boxX + boxW - padX;
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
  ctx.font = font("bold", 17);
  ctx.fillText(time, rightX, row1);
  const timeWidth = ctx.measureText(time).width;

  ctx.fillStyle = statusColor;
  ctx.fillText(statusText, rightX - timeWidth - 10 * scale, row1);

  // ── Divider ──
  ctx.strokeStyle = "rgba(6, 182, 212, 0.3)";
  ctx.lineWidth = 1 * scale;
  ctx.beginPath();
  ctx.moveTo(boxX + padX, divY);
  ctx.lineTo(boxX + boxW - padX, divY);
  ctx.stroke();

  // ── Row 2: Employee name ──
  ctx.textAlign = "left";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font("bold", 17);
  ctx.fillText(`👤 ${name}`, boxX + padX, row2);

  // ── Row 3: Date (left) + Coordinates (right) ──
  ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
  ctx.font = font("500", 14);
  ctx.textAlign = "left";
  ctx.fillText(`📅 ${date}`, boxX + padX, row3);

  if (location) {
    ctx.textAlign = "right";
    ctx.fillText(
      `📍 ${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`,
      rightX,
      row3
    );
  }

  // ── Row 4: Address (shortened to fit the available width) ──
  if (address) {
    ctx.textAlign = "left";
    const maxWidth = boxW - padX * 2;
    let shortAddr = address;
    while (ctx.measureText(`🏠 ${shortAddr}`).width > maxWidth && shortAddr.length > 10) {
      shortAddr = shortAddr.slice(0, -1);
    }
    if (shortAddr.length < address.length) shortAddr = `${shortAddr.trim()}...`;
    ctx.fillText(`🏠 ${shortAddr}`, boxX + padX, row4);
  }
}
